// CalendarEvent (DESIGN §2/§3): the compact calendar item used by the Calendar grids and lists.
//   <CalendarEvent item={calendarItem} variant="block" | "chip" | "row" clientName="Northwind" today={today} />
// Main Calendar = info blue, Internal Notes = muted violet, derived "Batch due" chips = neutral with a status dot,
// derived "Posting · <Client>" chips (DESIGN §9) = muted teal.
// Content is deliberately minimal: time · title · client. Extra props (pointer/click handlers, style, aria-*) pass
// through to the root element, so a grid can make blocks draggable; `resizable` adds a bottom-edge resize handle.
import './calendar-event.css';
import { cx, statusLabel } from './ui.jsx';
import { Icon } from '../icons.jsx';
import { CALENDAR_TYPES, batchLabel } from '../../shared/constants.js';
import { datePart, formatDate, timePart } from '../../shared/dates.js';

export const CALENDAR_NAMES = { ...Object.fromEntries(CALENDAR_TYPES.map((c) => [c.key, c.label])), posting: 'Posting' };
export const CALENDAR_TONES = { main: 'info', internal: 'violet', posting: 'teal' };

// '09:30' → '9:30am', '14:00' → '2pm'
export function compactTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return '';
  const h = Number(m[1]);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? 'am' : 'pm';
  return m[2] === '00' ? `${h12}${suffix}` : `${h12}:${m[2]}${suffix}`;
}

// '9 – 11am', '9:30 – 10am', '11am – 1pm' (same-day ranges; otherwise the start time alone)
export function compactRange(start, end) {
  const s = timePart(start);
  const e = end && datePart(end) === datePart(start) ? timePart(end) : '';
  if (!e) return compactTime(s);
  const sameHalf = (Number(s.slice(0, 2)) < 12) === (Number(e.slice(0, 2)) < 12);
  const a = compactTime(s);
  return `${sameHalf ? a.replace(/[ap]m$/, '') : a} – ${compactTime(e)}`;
}

// Full date/time line for a detail popover, in the same time style as the blocks: 'Fri, Sep 25, 2026 · 9 – 10am' /
// '… · All day'.
export function whenText(item) {
  const sd = datePart(item.start);
  let ed = item.end ? datePart(item.end) : sd;
  if (ed < sd) ed = sd;
  const day = (d) => `${formatDate(d, 'weekday')}, ${d.slice(0, 4)}`;
  if (item.allDay) return ed > sd ? `${formatDate(sd, 'weekday')} – ${day(ed)} · All day` : `${day(sd)} · All day`;
  if (ed > sd) {
    return `${formatDate(sd, 'weekday')}, ${compactTime(timePart(item.start))} – ${formatDate(ed, 'weekday')}, ${compactTime(timePart(item.end))}`;
  }
  return `${day(sd)} · ${compactRange(item.start, item.end)}`;
}

// Titles that repeat their kind ("Script Deadline: Barista Tips") drop the prefix — the calendar colour and the
// popover already say what it is. Batch-due items read "Client · Batch 03".
export function shortTitle(item) {
  if (item.source === 'batchDue') return item.title.replace(/ due$/, '');
  const prefix = `${item.kind}: `;
  if (item.kind && item.title.startsWith(prefix) && item.title.length > prefix.length) return item.title.slice(prefix.length);
  return item.title;
}

export const isBatchDue = (item) => item.source === 'batchDue';
export const isPosting = (item) => item.calendarType === 'posting';
// Derived items (batch due, posting) are never dragged or edited; they link to their batch / client.
export const isDerived = (item) => isBatchDue(item) || isPosting(item);
// A batch in an Off month is never late (DESIGN §7.5) — batch-due items carry `off` (see calendar.jsx withOff).
export const isOverdueItem = (item, today) => isBatchDue(item) && !item.off && item.status !== 'Done' && datePart(item.start) < today;

// Visual tone: calendar colour for events; batch-due chips carry their batch status (danger when overdue).
export function eventTone(item, today) {
  if (!isBatchDue(item)) return CALENDAR_TONES[item.calendarType] || 'info';
  if (item.status === 'Done') return 'positive';
  if (isOverdueItem(item, today)) return 'danger';
  if (item.status === 'Blocked') return 'danger';
  if (item.status === 'Review') return 'warning';
  return 'neutral';
}

// One-line accessible description that starts with the visible words (WCAG 2.5.3).
export function eventLabel(item, { clientName, today } = {}) {
  const parts = [isBatchDue(item) ? `Due: ${shortTitle(item)}` : shortTitle(item)];
  parts.push(item.allDay ? 'all day' : compactRange(item.start, item.end));
  if (isBatchDue(item) && item.clientName) parts.push([item.clientName, item.batchNumber ? batchLabel(item.batchNumber) : ''].filter(Boolean).join(' · '));
  else if (clientName && !isPosting(item)) parts.push(clientName);
  if (isPosting(item)) return parts.filter(Boolean).join(', ');
  if (isBatchDue(item)) parts.push(isOverdueItem(item, today) ? 'overdue' : statusLabel(item.status || 'To Do'));
  else parts.push(CALENDAR_NAMES[item.calendarType] || '');
  return parts.filter(Boolean).join(', ');
}

// variant 'block' — a timed block in the Week/Day grid (height in px decides how many lines fit).
// variant 'chip'  — one line (all-day row, month cells): [Due] title, or "9:30am title" for timed items (showTime).
// variant 'row'   — an agenda line: time column · colour bar · title / client.
export function CalendarEvent({
  item, variant = 'block', clientName = '', today = '', height = 48, showTime = false, resizable = false,
  selected = false, ghost = false, past = false, class: cls, ...rest
}) {
  const tone = eventTone(item, today);
  const due = isBatchDue(item);
  const quiet = due || isPosting(item); // the title already names the client
  const title = shortTitle(item) || '(No title)';
  const time = item.allDay ? '' : compactRange(item.start, item.end);
  const classes = cx(
    'cev', `cev-${variant}`, `tone-${tone}`, due && 'is-due', item.status === 'Done' && due && 'is-done',
    selected && 'is-selected', ghost && 'is-ghost', past && 'is-past', cls,
  );
  const label = eventLabel(item, { clientName, today });

  if (variant === 'row') {
    return (
      <button type="button" class={classes} aria-label={label} {...rest}>
        <span class="cev-row-time">{item.allDay ? (due ? 'Due' : 'All day') : compactTime(timePart(item.start))}</span>
        <span class="cev-bar" aria-hidden="true" />
        <span class="cev-row-main">
          <span class="cev-title">{title}</span>
          {(clientName && !quiet) || time ? (
            <span class="cev-meta">{[item.allDay ? '' : time, quiet ? '' : clientName].filter(Boolean).join(' · ')}</span>
          ) : null}
        </span>
        {due && item.status === 'Done' && <Icon name="check" size={14} class="cev-check" />}
      </button>
    );
  }

  if (variant === 'chip') {
    return (
      <button type="button" class={classes} aria-label={label} {...rest}>
        {due
          ? <span class="cev-tag">{item.status === 'Done' ? <Icon name="check" size={12} /> : 'Due'}</span>
          : !item.allDay && showTime ? <span class="cev-dot" aria-hidden="true" /> : null}
        {!item.allDay && showTime && <span class="cev-time">{compactTime(timePart(item.start))}</span>}
        <span class="cev-title">{title}</span>
      </button>
    );
  }

  // block: < 30px → one line "Title, 9am"; < 60px → title / time · client; taller → title (up to 2 lines) / time · client.
  const lines = height < 30 ? 1 : height < 60 ? 2 : 3;
  return (
    <div role="button" tabIndex={ghost ? -1 : 0} class={cx(classes, `cev-l${lines}`)} aria-label={label} {...rest}>
      {lines === 1 ? (
        <span class="cev-line"><span class="cev-title">{title}</span>{time && <span class="cev-time">, {compactTime(timePart(item.start))}</span>}</span>
      ) : (
        <>
          <span class="cev-title">{title}</span>
          <span class="cev-meta">{[time, clientName].filter(Boolean).join(' · ')}</span>
        </>
      )}
      {resizable && <span class="cev-resize" data-resize="true" aria-hidden="true" />}
    </div>
  );
}
